import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useEffect, useMemo, useRef, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { cpuTimeMs, peakMemoryBytes } from "../../shared/resourceUsage";
import {
  nextStepTestName,
  stepTestNamesFromFiles,
} from "../../shared/stepTests/constants";
import { useExecutionContent } from "../hooks/useExecutionContent";
import { useNavigateOnBranchChange } from "../hooks/useNavigateOnBranchChange";
import {
  useInFlightStepTestCapture,
  useStepExecutions,
} from "../hooks/useStepExecutions";
import type { StepTestFixtureSetupRequest } from "../lib/pinnedExecutions";
import { formatBytes } from "../lib/formatBytes";
import { pinContentIssue } from "../lib/pinEligibility";
import { navigate, routes, useCurrentUrl } from "../lib/urls";
import { Button } from "./Button";
import {
  CodeEditor,
  type CodeEditorHandle,
  type CodeSelection,
  type PinnedRange,
} from "./CodeEditor";
import { useCodeEditorSearch } from "./CodeEditor/useCodeEditorSearch";
import { CopyToggleButton } from "./CopyToggleButton";
import { Dropdown } from "./Dropdown";
import { EmptyState } from "./EmptyState";
import { ExecutionContent } from "./ExecutionContent";
import type { FileRenderControl } from "./FileRender";
import { FileRenderControlButton } from "./FileRenderControlButton";
import { ToggleButton, ToggleGroup } from "./HeadingToggle";
import type { IconName } from "./Icon";
import { Icon } from "./Icon";
import { RequestsList } from "./RequestsList";
import { ShortcutHint } from "./ShortcutHint";
import { StepDetails } from "./StepDetails";
import { StepTestFooter } from "./StepTestFooter";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { WavyLine } from "./WavyLine";
import type { CodeSnippet } from "./Chat/chatTypes";
import type { StepData } from "./Workflow";

const animalIcons: IconName[] = ["rabbit", "squirrel", "snail", "bird", "rat"];

export const formatTime = (time: number) => new Date(time).toLocaleString();

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

const HeadingOpener = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 4px 8px;
  background: transparent;
  border-radius: 9999px;

  &:hover {
    background: var(--alpha4);
  }
`;

const MetadataList = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetadataItem = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogOutput = styled("div")`
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;

// Bleed past StepDetails’ 12px Body padding so the table sits flush with the panel edges.
const RequestsBleed = styled("div")`
  margin: -12px;
  width: calc(100% + 24px);
  height: calc(100% + 24px);
`;

const BuildLogsButton = styled(Button)`
  &&& {
    background: var(--alpha4);
  }
  &&&:hover:not(:disabled) {
    background: var(--alpha8);
  }
`;

type Props = {
  step: StepData;
  branchId: string;
  commitId: string;
  files: readonly { path: string }[];
  isMainBranch: boolean;
  pinnedByExecId?: Map<string, string>;
  onSelectionChange?: (selections: CodeSelection[]) => void;
  codeSnippets?: readonly CodeSnippet[];
  isBeingWritten?: boolean;
  onInvestigateFailure: (params: { stepDir: string; testName: string }) => void;
  onAskSetupStepTest: (request: StepTestFixtureSetupRequest) => void;
};

export function StepExecutions({
  step,
  branchId,
  commitId,
  files,
  isMainBranch,
  pinnedByExecId,
  onSelectionChange,
  codeSnippets,
  isBeingWritten,
  onInvestigateFailure,
  onAskSetupStepTest,
}: Props) {
  const {
    workflowId,
    execution: selectedExecutionId,
    view,
  } = useCurrentUrl(routes.space.workflow);
  const selectedView = view ?? "output";
  const setSelectedView = (v: string) =>
    navigate(null, { view: v }, { replace: true });

  const z = useZero();
  const navigateOnBranchChange = useNavigateOnBranchChange(branchId);

  // `files` paths are already step-relative — selectStepFiles strips the step
  // prefix upstream — so match the step-test files directly, without re-stripping.
  const stepTestNames = useMemo(() => stepTestNamesFromFiles(files), [files]);

  useEffect(() => {
    const views: Record<string, string> = {
      Digit1: "output",
      Digit2: "input",
      Digit3: "logs",
      Digit4: "metadata",
      Digit5: "requests",
    };
    function handleKeyDown(e: KeyboardEvent) {
      if (!e.altKey || !(e.metaKey || e.ctrlKey) || e.shiftKey) return;
      const v = views[e.code];
      if (v) {
        e.preventDefault();
        setSelectedView(v);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const [animalIcon] = useState(
    () =>
      animalIcons[Math.floor(Math.random() * animalIcons.length)] ?? "rabbit"
  );
  const { executions } = useStepExecutions(workflowId, branchId, step.id);
  const inFlightCapture = useInFlightStepTestCapture(
    workflowId,
    branchId,
    !isMainBranch ? step.id : null
  );
  const isCapturingFixtures = inFlightCapture != null;
  const selectedExecution =
    executions.find((execution) => execution.id === selectedExecutionId) ??
    executions[0];
  const [animateFooter, setAnimateFooter] = useState(false);
  const [namingExecId, setNamingExecId] = useState<string | null>(null);
  const [prevExecId, setPrevExecId] = useState(selectedExecution?.id);
  if (selectedExecution?.id !== prevExecId) {
    setPrevExecId(selectedExecution?.id);
    setAnimateFooter(false);
    setNamingExecId(null);
  }
  // Auto-naming is best-effort: the model returns NONE for a generic run, leaving
  // the placeholder name with no rename to observe, so stop showing "Naming
  // test…" after a bounded wait instead of shimmering forever.
  useEffect(() => {
    if (namingExecId == null) return;
    const id = setTimeout(() => setNamingExecId(null), 10_000);
    return () => clearTimeout(id);
  }, [namingExecId]);
  const fullOutput = useExecutionContent(
    selectedExecution,
    "output",
    selectedView === "output" || selectedView === "metadata"
  );
  const fullInput = useExecutionContent(
    selectedExecution,
    "input",
    selectedView === "input" || selectedView === "output"
  );
  const fullLog = useExecutionContent(
    selectedExecution,
    "log",
    selectedView === "logs" || selectedView === "metadata"
  );
  const fullRequests = useExecutionContent(
    selectedExecution,
    "requests",
    selectedView === "requests"
  );
  const activeContent =
    selectedView === "output"
      ? fullOutput
      : selectedView === "input"
        ? fullInput
        : undefined;
  const pendingBuild = executions.find(
    (e) =>
      e.kind === "build" &&
      e.status === "pending" &&
      e.buildKey === selectedExecution?.buildKey &&
      e.contentHash === selectedExecution?.contentHash
  );
  const isWaitingForBuild =
    selectedExecution &&
    selectedExecution.kind === "run" &&
    selectedExecution?.status === "pending" &&
    pendingBuild != null;
  const buildForSelectedRun =
    selectedExecution && selectedExecution.kind === "run"
      ? executions.find(
          (e) =>
            e.kind === "build" &&
            e.buildKey === selectedExecution.buildKey &&
            e.contentHash === selectedExecution.contentHash &&
            e.createdAt <= selectedExecution.createdAt
        )
      : undefined;
  const failedBuildId =
    selectedExecution?.kind === "run" && selectedExecution.status === "error"
      ? (selectedExecution.failedBuildExecutionId ??
        (buildForSelectedRun?.status === "error"
          ? buildForSelectedRun.id
          : undefined))
      : undefined;
  const failedBuild = failedBuildId != null;
  const goToFailedBuild = failedBuildId
    ? () =>
        navigate(
          null,
          { execution: failedBuildId, view: "logs" },
          { replace: true }
        )
    : undefined;

  const logEditorRef = useRef<CodeEditorHandle | null>(null);
  const stickToBottomRef = useRef(true);
  const search = useCodeEditorSearch(selectedView);

  const logPinnedRanges = useMemo<PinnedRange[] | undefined>(() => {
    const expected = `${step.name}/log.txt`;
    const matching = codeSnippets?.filter((s) => s.filePath === expected);
    return matching?.length
      ? matching.map((s) => ({ from: s.from, to: s.to }))
      : undefined;
  }, [codeSnippets, step.name]);
  const isStreaming =
    selectedExecution?.status === "pending" && !!fullLog.content;
  const logContent = fullLog.content;
  useEffect(() => {
    if (!isStreaming || !logContent) return;
    if (stickToBottomRef.current) logEditorRef.current?.scrollToBottom();
  }, [isStreaming, logContent]);

  const requestsEndRef = useRef<HTMLDivElement>(null);
  const isRequestsStreaming =
    selectedExecution?.status === "pending" && !!fullRequests.content;
  const requestsContent = fullRequests.content;
  useEffect(() => {
    if (isRequestsStreaming && requestsContent) {
      requestsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isRequestsStreaming, requestsContent]);

  const [fileControls, setFileControls] = useState<FileRenderControl[]>([]);

  const copyableContent =
    selectedView === "output" || selectedView === "input"
      ? activeContent?.content
      : selectedView === "logs"
        ? fullLog.content
        : selectedView === "requests"
          ? fullRequests.content
          : undefined;

  // The pin needs the run’s input+output, which only load on the output view, so
  // it lives there.
  const pinRun =
    selectedView === "output" &&
    selectedExecution?.kind === "run" &&
    selectedExecution.status === "success"
      ? selectedExecution
      : null;
  const issue = pinContentIssue(fullOutput, fullInput);
  const canPin = pinRun != null && issue === null;
  const pinnedName = pinRun ? (pinnedByExecId?.get(pinRun.id) ?? null) : null;
  const isPinned = pinnedName !== null;
  const activeTestName = selectedExecution
    ? (pinnedByExecId?.get(selectedExecution.id) ?? null)
    : null;
  // On the live workflow every pin routes through 3B: the hand-off forks a
  // branch with the agent attached and pins there, instead of stranding the
  // test on a throwaway fork the way a direct client pin on main would. Storage
  // steps always hand off, so the agent can author their volume fixtures. On a
  // draft branch a non-storage pin lands in place, one click.
  const addStepTest = () => {
    if (!pinRun) return;
    setAnimateFooter(stepTestNames.length === 0);
    if (isMainBranch || step.hasStorage) {
      onAskSetupStepTest({ stepDir: step.id, executionId: pinRun.id });
    } else {
      setNamingExecId(pinRun.id);
      void navigateOnBranchChange((branchParams) =>
        z.mutate(
          sharedMutators.pinStepTest({
            workflowId,
            ...branchParams,
            stepDir: step.id,
            executionId: pinRun.id,
            name: nextStepTestName(stepTestNames),
            input: fullInput.content ?? "",
            expectedOutput: fullOutput.content ?? "",
            autoName: true,
          })
        )
      );
    }
  };
  const viewItems = [
    {
      type: "button" as const,
      label: "Output",
      left: <Icon name="output" size={16} />,
      right:
        selectedView === "output" ? (
          <Icon name="check" size={12} color="var(--text-secondary)" />
        ) : (
          <ShortcutHint>⌘⌥1</ShortcutHint>
        ),
      onClick: () => setSelectedView("output"),
    },
    {
      type: "button" as const,
      label: "Input",
      left: <Icon name="input" size={16} />,
      right:
        selectedView === "input" ? (
          <Icon name="check" size={12} color="var(--text-secondary)" />
        ) : (
          <ShortcutHint>⌘⌥2</ShortcutHint>
        ),
      onClick: () => setSelectedView("input"),
    },
    {
      type: "button" as const,
      label: "Logs",
      left: <Icon name="logs" size={16} />,
      right:
        selectedView === "logs" ? (
          <Icon name="check" size={12} color="var(--text-secondary)" />
        ) : (
          <ShortcutHint>⌘⌥3</ShortcutHint>
        ),
      onClick: () => setSelectedView("logs"),
    },
    {
      type: "button" as const,
      label: "Metadata",
      left: <Icon name="info" size={16} />,
      right:
        selectedView === "metadata" ? (
          <Icon name="check" size={12} color="var(--text-secondary)" />
        ) : (
          <ShortcutHint>⌘⌥4</ShortcutHint>
        ),
      onClick: () => setSelectedView("metadata"),
    },
    {
      type: "button" as const,
      label: "Requests",
      left: <Icon name="agent-open" size={16} />,
      right:
        selectedView === "requests" ? (
          <Icon name="check" size={12} color="var(--text-secondary)" />
        ) : (
          <ShortcutHint>⌘⌥5</ShortcutHint>
        ),
      onClick: () => setSelectedView("requests"),
    },
  ];

  return (
    <search.Provider>
      <StepDetails
        subheader={search.subheader}
        animateFooter={animateFooter}
        footer={
          selectedView === "output" && stepTestNames.length > 0 ? (
            <StepTestFooter
              workflowId={workflowId}
              branchId={branchId}
              commitId={commitId}
              stepDir={step.id}
              testNames={stepTestNames}
              activeTestName={activeTestName}
              naming={
                namingExecId != null && namingExecId === selectedExecution?.id
              }
              capturing={isCapturingFixtures}
              onInvestigateFailure={onInvestigateFailure}
            />
          ) : undefined
        }
        headingRight={
          <ToggleGroup>
            {pinRun && !isPinned && (
              <Tooltip content={issue ?? "Add run as step test"}>
                <ToggleButton
                  aria-label="Add run as step test"
                  disabled={!canPin}
                  onPress={() => addStepTest()}
                >
                  <Icon name="microscope" size={12} />
                </ToggleButton>
              </Tooltip>
            )}
            {copyableContent && (
              <CopyToggleButton getText={() => copyableContent} />
            )}
            {search.toggleButton}
            {fileControls.map((control) => (
              <FileRenderControlButton key={control.id} control={control} />
            ))}
          </ToggleGroup>
        }
        heading={
          <Dropdown
            align="bottom-left"
            opener={
              <HeadingOpener>
                <span style={{ display: "flex" }}>
                  {selectedView === "output" ? (
                    <Icon name="output" size={12} />
                  ) : selectedView === "input" ? (
                    <Icon name="input" size={12} />
                  ) : selectedView === "logs" ? (
                    <Icon name="logs" size={12} />
                  ) : selectedView === "requests" ? (
                    <Icon name="agent-open" size={12} />
                  ) : (
                    <Icon name="info" size={12} />
                  )}
                </span>
                <Text variant="text-small-md">
                  {selectedView === "output"
                    ? "Output"
                    : selectedView === "input"
                      ? "Input"
                      : selectedView === "logs"
                        ? "Logs"
                        : selectedView === "requests"
                          ? "Requests"
                          : "Metadata"}
                </Text>
                <Icon name="chevron-down" size={12} />
              </HeadingOpener>
            }
            sections={[
              {
                label: "View",
                items: viewItems,
              },
            ]}
          />
        }
        body={
          selectedView === "output" ? (
            selectedExecution &&
            selectedExecution.kind === "build" &&
            selectedExecution.status === "pending" ? (
              <EmptyState icon={<WavyLine />} maxWidth={220} title="Building" />
            ) : selectedExecution &&
              selectedExecution.kind === "build" &&
              selectedExecution.status === "success" ? (
              <EmptyState
                iconName="package-2"
                iconSize={24}
                maxWidth={220}
                title="Built successfully"
                description="Dependencies are installed and up to date."
              />
            ) : isWaitingForBuild ? (
              <EmptyState
                icon={<WavyLine />}
                maxWidth={220}
                title="Waiting for build"
                description="This run will start once the build finishes."
              />
            ) : failedBuild ? (
              <EmptyState
                iconName="heart-crack"
                iconSize={24}
                maxWidth={220}
                title="Build failed"
                description="This step couldn't run because the build failed."
                action={
                  <BuildLogsButton
                    variant="secondary"
                    onClick={goToFailedBuild}
                  >
                    View build logs
                  </BuildLogsButton>
                }
              />
            ) : selectedExecution ? (
              <ExecutionContent
                execution={selectedExecution}
                onShowLogs={() => setSelectedView("logs")}
                stepName={step.name}
                edgeRoute={step.edgeRoute ?? undefined}
                onSelectionChange={onSelectionChange}
                codeSnippets={codeSnippets}
                onControls={setFileControls}
              />
            ) : isBeingWritten ? (
              <EmptyState
                icon={<WavyLine />}
                maxWidth={220}
                title="Taking shape"
                description="3B is still writing this step."
              />
            ) : (
              <EmptyState
                iconName={animalIcon}
                iconSize={24}
                maxWidth={220}
                title="No output yet"
                description="Run this step to see its output here."
              />
            )
          ) : selectedView === "input" ? (
            selectedExecution ? (
              <ExecutionContent
                field="input"
                execution={selectedExecution}
                stepName={step.name}
                onSelectionChange={onSelectionChange}
                codeSnippets={codeSnippets}
                onControls={setFileControls}
              />
            ) : (
              <EmptyState
                iconName={animalIcon}
                iconSize={24}
                maxWidth={220}
                title="No input"
                description="This step wasn't triggered with any input."
              />
            )
          ) : selectedView === "logs" ? (
            failedBuild ? (
              <EmptyState
                iconName="heart-crack"
                iconSize={24}
                maxWidth={220}
                title="Build failed"
                description="This step couldn't run because the build failed."
                action={
                  <BuildLogsButton
                    variant="secondary"
                    onClick={goToFailedBuild}
                  >
                    View build logs
                  </BuildLogsButton>
                }
              />
            ) : fullLog.content ? (
              <CodeEditor
                ref={logEditorRef}
                filePath="log.txt"
                value={fullLog.content}
                onSelectionChange={onSelectionChange}
                pinnedRanges={logPinnedRanges}
                onVisibleLineChange={(line) => {
                  stickToBottomRef.current =
                    line == null
                      ? true
                      : (logEditorRef.current?.isAtBottom() ?? true);
                }}
              />
            ) : fullLog.loading ? null : selectedExecution &&
              selectedExecution.kind === "build" &&
              selectedExecution.status === "pending" ? (
              <EmptyState icon={<WavyLine />} maxWidth={220} title="Building" />
            ) : isWaitingForBuild ? (
              <EmptyState
                icon={<WavyLine />}
                maxWidth={220}
                title="Waiting for build"
                description="This run will start once the build finishes."
              />
            ) : selectedExecution?.status === "pending" ? (
              <EmptyState
                icon={<WavyLine />}
                maxWidth={220}
                title="Running"
                description="Hang tight, logs will appear when this step runs."
              />
            ) : (
              <LogOutput>
                <Text variant="code-regular" color="var(--text-secondary)">
                  Nothing logged
                </Text>
              </LogOutput>
            )
          ) : selectedView === "requests" ? (
            failedBuild ? (
              <EmptyState
                iconName="heart-crack"
                iconSize={24}
                maxWidth={220}
                title="Build failed"
                description="This step couldn't run because the build failed."
                action={
                  <BuildLogsButton
                    variant="secondary"
                    onClick={goToFailedBuild}
                  >
                    View build logs
                  </BuildLogsButton>
                }
              />
            ) : fullRequests.content ? (
              <RequestsBleed>
                <RequestsList
                  content={fullRequests.content}
                  executionId={selectedExecution?.id}
                  bottomRef={requestsEndRef}
                />
              </RequestsBleed>
            ) : fullRequests.loading ? null : selectedExecution &&
              selectedExecution.kind === "build" &&
              selectedExecution.status === "pending" ? (
              <EmptyState icon={<WavyLine />} maxWidth={220} title="Building" />
            ) : isWaitingForBuild ? (
              <EmptyState
                icon={<WavyLine />}
                maxWidth={220}
                title="Waiting for build"
                description="This run will start once the build finishes."
              />
            ) : selectedExecution?.status === "pending" ? (
              <EmptyState
                icon={<WavyLine />}
                maxWidth={220}
                title="Running"
                description="Hang tight, requests will appear when this step runs."
              />
            ) : (
              <LogOutput>
                <Text variant="code-regular" color="var(--text-secondary)">
                  No requests
                </Text>
              </LogOutput>
            )
          ) : selectedExecution && selectedExecution.status !== "pending" ? (
            <MetadataList>
              {selectedExecution.duration != null &&
                selectedExecution.completedAt != null && (
                  <MetadataItem>
                    <Text variant="text-regular" color="var(--text-secondary)">
                      Queue time
                    </Text>
                    <Text variant="text-regular">
                      {formatDuration(
                        selectedExecution.completedAt -
                          selectedExecution.createdAt -
                          selectedExecution.duration
                      )}
                    </Text>
                  </MetadataItem>
                )}
              {selectedExecution.duration != null && (
                <MetadataItem>
                  <Text variant="text-regular" color="var(--text-secondary)">
                    Run time
                  </Text>
                  <Text variant="text-regular">
                    {formatDuration(selectedExecution.duration)}
                  </Text>
                </MetadataItem>
              )}
              {selectedExecution.result != null && (
                <>
                  <MetadataItem>
                    <Text variant="text-regular" color="var(--text-secondary)">
                      Peak memory
                    </Text>
                    <Text variant="text-regular">
                      {formatBytes(peakMemoryBytes(selectedExecution.result))}
                    </Text>
                  </MetadataItem>
                  <MetadataItem>
                    <Text variant="text-regular" color="var(--text-secondary)">
                      CPU time
                    </Text>
                    <Text variant="text-regular">
                      {formatDuration(cpuTimeMs(selectedExecution.result))}
                    </Text>
                  </MetadataItem>
                </>
              )}
              <MetadataItem>
                <Text variant="text-regular" color="var(--text-secondary)">
                  Output size
                </Text>
                <Text variant="text-regular">
                  {fullOutput.size != null ? formatBytes(fullOutput.size) : "…"}
                </Text>
              </MetadataItem>
              <MetadataItem>
                <Text variant="text-regular" color="var(--text-secondary)">
                  Log size
                </Text>
                <Text variant="text-regular">
                  {fullLog.size != null ? formatBytes(fullLog.size) : "…"}
                </Text>
              </MetadataItem>
              <MetadataItem>
                <Text variant="text-regular" color="var(--text-secondary)">
                  Status
                </Text>
                <Text
                  variant="text-regular"
                  color={
                    selectedExecution.status === "success"
                      ? "var(--success)"
                      : "var(--error)"
                  }
                >
                  {selectedExecution.status}
                </Text>
              </MetadataItem>
            </MetadataList>
          ) : selectedExecution &&
            selectedExecution.kind === "build" &&
            selectedExecution.status === "pending" ? (
            <EmptyState icon={<WavyLine />} maxWidth={220} title="Building" />
          ) : isWaitingForBuild ? (
            <EmptyState
              icon={<WavyLine />}
              maxWidth={220}
              title="Waiting for build"
              description="This run will start once the build finishes."
            />
          ) : selectedExecution?.status === "pending" ? (
            <EmptyState
              icon={<WavyLine />}
              maxWidth={220}
              title="Running"
              description="Hang tight, metadata will appear when this step finishes."
            />
          ) : isBeingWritten ? (
            <EmptyState
              icon={<WavyLine />}
              maxWidth={220}
              title="Taking shape"
              description="3B is still writing this step."
            />
          ) : (
            <EmptyState
              iconName={animalIcon}
              iconSize={24}
              maxWidth={220}
              title="No output yet"
              description="Run this step to see its output here."
            />
          )
        }
      />
    </search.Provider>
  );
}
